/**
 * Ornamental Trees – Accessibility fixes
 * Targets: WCAG 2.1 AA / UK Equality Act 2010
 *
 * What this file does (and nothing more):
 *
 *  1. Makes <side-drawer-toggle> keyboard-operable (Enter / Space)
 *     Root cause of Jason's complaint – the custom element only had a click
 *     listener, so keyboard users could never open the "Choose Option" drawer.
 *
 *  2. Makes <add-product> keyboard-operable (Enter / Space)
 *     Same problem: AJAX add-to-cart never fires for keyboard users.
 *
 *  3. Removes the mobile menu from the tab order when it is closed.
 *     When the hamburger menu is closed the entire <aside id="mobilemenu">
 *     stays in the DOM with all its links still focusable, creating hundreds
 *     of invisible tab stops – exactly the "blank area" focus Jason reported.
 *     We hook directly into mobilemenuShow / mobilemenuHide so there is no
 *     MutationObserver loop and no conflict with existing JS.
 *
 *  NO existing JS is modified. This file is purely additive.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
   * UTIL: safely upgrade an element once
   * ───────────────────────────────────────────────────────────────────────── */
  function upgradeOnce(el, fn) {
    if (el._a11yUpgraded) return;
    el._a11yUpgraded = true;
    fn(el);
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * FIX 1 – <side-drawer-toggle> keyboard support
   *
   * drawer-toggle.js only adds a "click" listener inside the constructor.
   * We add role="button" + tabindex="0" + keydown handler so Enter/Space
   * call .click() which triggers the existing drawer-toggle event dispatch.
   * ───────────────────────────────────────────────────────────────────────── */
  function upgradeDrawerToggle(el) {
    upgradeOnce(el, function (toggle) {
      toggle.setAttribute('role', 'button');
      toggle.setAttribute('tabindex', '0');

      var drawerId = toggle.getAttribute('data-drawer');
      if (drawerId) {
        toggle.setAttribute('aria-haspopup', 'dialog');
        toggle.setAttribute('aria-controls', drawerId);
        toggle.setAttribute('aria-expanded', 'false');
      }

      toggle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle.click(); // triggers existing SideDrawerToggle click handler
        }
      });
    });
  }

  function upgradeAllDrawerToggles() {
    document.querySelectorAll('side-drawer-toggle').forEach(upgradeDrawerToggle);
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * FIX 2 – <add-product> keyboard support
   *
   * global.js AddProduct only adds a "click" listener in the constructor.
   * Same pattern as Fix 1.
   * ───────────────────────────────────────────────────────────────────────── */
  function upgradeAddProduct(el) {
    upgradeOnce(el, function (btn) {
      btn.setAttribute('role', 'button');
      btn.setAttribute('tabindex', '0');

      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click(); // triggers existing AddProduct click → addProduct()
        }
      });
    });
  }

  function upgradeAllAddProducts() {
    document.querySelectorAll('add-product').forEach(upgradeAddProduct);
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * FIX 3 – Mobile menu inert when closed
   *
   * ua_mobilemenu.js exposes mobilemenuShow() and mobilemenuHide() on the
   * <mobile-menu> custom element. We wait for the element to be defined and
   * connected, then wrap those two methods to toggle the `inert` attribute
   * on the inner <aside id="mobilemenu">.
   *
   * `inert` removes ALL descendants from the tab order AND accessibility tree
   * in one attribute – the modern, correct solution (supported in all
   * evergreen browsers including Chrome 102+, Firefox 112+, Safari 15.5+).
   * ───────────────────────────────────────────────────────────────────────── */
  function patchMobileMenu(mobileMenuEl) {
    if (mobileMenuEl._a11yPatched) return;
    mobileMenuEl._a11yPatched = true;

    var aside = mobileMenuEl.querySelector('aside#mobilemenu');
    if (!aside) return;

    // Start inert – menu is closed on page load
    aside.setAttribute('inert', '');

    // Wrap mobilemenuShow
    var originalShow = mobileMenuEl.mobilemenuShow.bind(mobileMenuEl);
    mobileMenuEl.mobilemenuShow = function () {
      aside.removeAttribute('inert');
      originalShow();
    };

    // Wrap mobilemenuHide
    var originalHide = mobileMenuEl.mobilemenuHide.bind(mobileMenuEl);
    mobileMenuEl.mobilemenuHide = function () {
      originalHide();
      aside.setAttribute('inert', '');
    };
  }

  function findAndPatchMobileMenu() {
    var el = document.querySelector('mobile-menu');
    if (el && typeof el.mobilemenuShow === 'function') {
      patchMobileMenu(el);
      return true;
    }
    return false;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * INIT – run on DOMContentLoaded, then watch for dynamic content
   * ───────────────────────────────────────────────────────────────────────── */
  function init() {
    upgradeAllDrawerToggles();
    upgradeAllAddProducts();

    // mobile-menu methods may not be available yet if the custom element
    // hasn't run connectedCallback. Poll briefly then fall back to observer.
    if (!findAndPatchMobileMenu()) {
      var attempts = 0;
      var poll = setInterval(function () {
        attempts++;
        if (findAndPatchMobileMenu() || attempts > 20) {
          clearInterval(poll);
        }
      }, 100);
    }

    // Watch for side-drawer or add-product elements added after initial render
    // (e.g. quick-add modals, AJAX section refreshes)
    var observer = new MutationObserver(function (mutations) {
      var needDrawer = false;
      var needAddProduct = false;

      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches('side-drawer-toggle') || node.querySelector('side-drawer-toggle')) needDrawer = true;
          if (node.matches('add-product') || node.querySelector('add-product')) needAddProduct = true;
        });
      });

      if (needDrawer) upgradeAllDrawerToggles();
      if (needAddProduct) upgradeAllAddProducts();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* Desktop mega menu tabindex fix + product drawer focus trap */
(function () {
  'use strict';

  var drawerFocusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  var megaLinkSelector =
    'mega-menu-item a[href], mega-menu-item button:not([disabled])';

function closeMegaMenuVisualState() {
  document.dispatchEvent(new CustomEvent('a11y:megamenu:close'));

  document.body.classList.remove('megamenu--isActive');

  document.querySelectorAll('.megamenu--current').forEach(function (el) {
    el.classList.remove('megamenu--current');
  });

  document.querySelectorAll('.megamenu--isVisible').forEach(function (el) {
    el.classList.remove('megamenu--isVisible');
  });

  document.querySelectorAll('#megamenu.megamenu--animate, .megamenu.megamenu--animate').forEach(function (el) {
    el.classList.remove('megamenu--animate');
  });
}

  function disableAllMegaLinks() {
    document.querySelectorAll(megaLinkSelector).forEach(function (el) {
      el.setAttribute('tabindex', '-1');
    });

    document.querySelectorAll('.header__inline-menu .megamenu-item[data-title]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove('active', 'is-active', 'open', 'is-open');
    });

    closeMegaMenuVisualState();
  }

  function enableMegaLinks(trigger) {
    if (!trigger) return;

    var title = trigger.getAttribute('data-title');
    var megaItem = document.getElementById('megamenu-' + title);
    if (!megaItem) return;

    disableAllMegaLinks();

    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-controls', megaItem.id);

    megaItem.querySelectorAll('a[href], button:not([disabled])').forEach(function (el) {
      el.removeAttribute('tabindex');
    });

    trigger.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
  }

  function initMegaMenuA11y() {
    disableAllMegaLinks();

    function getTriggers() {
      return Array.from(
        document.querySelectorAll('.header__inline-menu .megamenu-item[data-title]')
      );
    }

    function getLinks(megaItem) {
      return Array.from(
        megaItem.querySelectorAll('a[href], button:not([disabled])')
      ).filter(function (el) {
        return el.getAttribute('tabindex') !== '-1';
      });
    }

    function focusTopMenu(trigger, direction) {
      var triggers = getTriggers();
      var index = triggers.indexOf(trigger);
      var next = triggers[index + direction];

      disableAllMegaLinks();
      closeMegaMenuVisualState();

      if (next) {
        next.focus();
      } else {
        var firstMain = document.querySelector(
          'main a[href], main button:not([disabled]), main input:not([disabled]), main select:not([disabled]), main textarea:not([disabled]), main [tabindex]:not([tabindex="-1"])'
        );

        if (firstMain) firstMain.focus();
      }
    }

    document.querySelectorAll('.header__inline-menu .megamenu-item[data-title]').forEach(function (trigger) {
      var title = trigger.getAttribute('data-title');
      var megaItem = document.getElementById('megamenu-' + title);
      if (!megaItem) return;

      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', megaItem.id);

      trigger.addEventListener('mouseenter', function () {
        enableMegaLinks(trigger);
      });

      trigger.addEventListener('focus', function () {
        disableAllMegaLinks();
        closeMegaMenuVisualState();
      });
    });

    document.addEventListener(
      'keydown',
      function (e) {
        var active = document.activeElement;

        var trigger = active.closest
          ? active.closest('.header__inline-menu .megamenu-item[data-title]')
          : null;

        var activeMegaItem = active.closest
          ? active.closest('mega-menu-item')
          : null;

        if (trigger) {
          var title = trigger.getAttribute('data-title');
          var megaItem = document.getElementById('megamenu-' + title);

          if (!megaItem) return;

          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            enableMegaLinks(trigger);

            var firstLink = getLinks(megaItem)[0];
            if (firstLink) firstLink.focus();
          }

          if (e.key === 'ArrowRight') {
            e.preventDefault();
            focusTopMenu(trigger, 1);
          }

          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            focusTopMenu(trigger, -1);
          }

          if (e.key === 'Tab' && !e.shiftKey) {
            disableAllMegaLinks();
            closeMegaMenuVisualState();
            return;
          }

          if (e.key === 'Escape') {
            e.preventDefault();
            disableAllMegaLinks();
            closeMegaMenuVisualState();
            trigger.focus();
          }

          return;
        }

        if (activeMegaItem) {
          var links = getLinks(activeMegaItem);
          if (!links.length) return;

          var currentIndex = links.indexOf(active);
          var first = links[0];
          var last = links[links.length - 1];

          var currentTitle = activeMegaItem.id.replace('megamenu-', '');
          var parentTrigger = document.querySelector(
            '.header__inline-menu .megamenu-item[data-title="' + currentTitle + '"]'
          );

          if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            disableAllMegaLinks();
            closeMegaMenuVisualState();

            if (parentTrigger) {
              focusTopMenu(parentTrigger, 1);
            }

            return;
          }

          if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            disableAllMegaLinks();
            closeMegaMenuVisualState();

            if (parentTrigger) {
              parentTrigger.focus();
            }

            return;
          }

          if (e.key === 'ArrowDown') {
            e.preventDefault();

            if (active === last) {
              first.focus();
            } else {
              links[currentIndex + 1].focus();
            }
          }

          if (e.key === 'ArrowUp') {
            e.preventDefault();

            if (active === first) {
              last.focus();
            } else {
              links[currentIndex - 1].focus();
            }
          }

          if (e.key === 'ArrowRight') {
            e.preventDefault();

            if (parentTrigger) {
              focusTopMenu(parentTrigger, 1);
            }
          }

          if (e.key === 'ArrowLeft') {
            e.preventDefault();

            if (parentTrigger) {
              focusTopMenu(parentTrigger, -1);
            }
          }

          if (e.key === 'Escape') {
            e.preventDefault();
            disableAllMegaLinks();
            closeMegaMenuVisualState();

            if (parentTrigger) parentTrigger.focus();
          }
        }
      },
      true
    );

    document.addEventListener('focusin', function (e) {
      if (
        !e.target.closest('.header__inline-menu .megamenu-item[data-title]') &&
        !e.target.closest('mega-menu-item')
      ) {
        disableAllMegaLinks();
        closeMegaMenuVisualState();
      }
    });
  }

  function getOpenDrawer() {
    return document.querySelector(
      'side-drawer.active, side-drawer.is-active, side-drawer[open], .side-drawer.active, .side-drawer.is-active, .side-drawer[open]'
    );
  }

  function trapDrawerFocus(e) {
    if (e.key !== 'Tab') return;

    var drawer = getOpenDrawer();
    if (!drawer) return;

    var focusable = Array.from(drawer.querySelectorAll(drawerFocusableSelector)).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });

    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function focusDrawerOnOpen() {
    document.querySelectorAll('side-drawer-toggle[data-drawer]').forEach(function (toggle) {
      if (toggle._a11yDrawerFocusAdded) return;
      toggle._a11yDrawerFocusAdded = true;

      toggle.addEventListener('click', function () {
        var drawerId = toggle.getAttribute('data-drawer');
        var drawer = document.getElementById(drawerId);

        setTimeout(function () {
          if (!drawer) return;

          var first = drawer.querySelector(
            'side-drawer-toggle, button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );

          if (first) first.focus();
        }, 250);
      });
    });
  }

  function init() {
    initMegaMenuA11y();
    focusDrawerOnOpen();
    document.addEventListener('keydown', trapDrawerFocus);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();